import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/api';
import { Eye, EyeOff, Loader2, AlertTriangle } from 'lucide-react';
import { extractAxiosError } from '../components/SafeError';
import AuthHeader from '../components/AuthHeader';

const ChangePasswordPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Password validation - must match registration requirements
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!/[a-zA-Z]/.test(newPassword)) {
      setError('Password must contain at least one letter');
      return;
    }
    if (!/\d/.test(newPassword)) {
      setError('Password must contain at least one number');
      return;
    }

    setLoading(true);

    try {
      await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword
      });
      navigate('/dashboard');
    } catch (err) {
      setError(extractAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cockpit-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md" data-testid="change-password-page">
        <div className="card-panel p-8">
          {/* Header */}
          <AuthHeader />
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-status-warning/20 rounded-sm mb-4">
              <AlertTriangle className="w-6 h-6 text-status-warning" />
            </div>
            <h1 className="text-2xl font-semibold text-cockpit-text-primary">Password Change Required</h1>
            <p className="text-sm text-cockpit-text-secondary mt-1">
              You must change your password before continuing
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-6 p-3 bg-red-900/30 border border-red-900 rounded-sm">
              <p className="text-sm text-red-300" data-testid="change-password-error">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label-text block mb-2">Current/Temporary Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input-field pr-10"
                  placeholder="Enter current password"
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-cockpit-text-secondary hover:text-cockpit-text-primary"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="label-text block mb-2">New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input-field"
                placeholder="Enter new password"
                required
                data-testid="new-password-input"
              />
              <p className="text-xs text-cockpit-text-secondary mt-1">
                Must be at least 8 characters with at least one letter and one number
              </p>
            </div>

            <div>
              <label className="label-text block mb-2">Confirm New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-field"
                placeholder="Confirm new password"
                required
                data-testid="confirm-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2"
              data-testid="change-password-submit"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
